import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavigationProvider } from '@/context/NavigationContext'
import MainLayout from '@/components/layout/MainLayout'
import { useSettingsStore, initializeSettings } from '@/store/settings.store'
import { initializeHabits } from '@/store/habit.store'
import { initializeWeeks } from '@/store/weekly.store'
import { initializeTasks } from '@/store/tasks.store'
import { initializeFinance } from '@/store/finance.store'
import { syncStorage } from '@/lib/chromeStorageAdapter'

function AppContent() {
  const { i18n } = useTranslation()
  const theme = useSettingsStore((state) => state.theme)
  const [isInitialized, setIsInitialized] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)

  // Apply dark mode immediately on mount before anything else
  useEffect(() => {
    console.log('🌙 Applying initial dark theme')
    document.documentElement.classList.add('dark')
  }, [])

  // Initialize app and all stores from Chrome Storage
  useEffect(() => {
    const initialize = async () => {
      try {
        console.log('⏳ Initializing app...')

        // First initialize the sync storage cache
        await syncStorage.initialize()
        console.log('✅ Sync storage initialized')

        // Rehydrate Zustand stores from Chrome Storage (this handles all persist stores)
        await useSettingsStore.persist.rehydrate()
        console.log('✅ Settings rehydrated from Chrome Storage')

        // Then load all other stores in parallel
        const results = await Promise.allSettled([
          initializeHabits(),
          initializeWeeks(),
          initializeTasks(),
          initializeFinance(),
        ])

        // Check for any initialization errors
        const errors = results.filter(
          (r) => r.status === 'rejected'
        ) as PromiseRejectedResult[]
        if (errors.length > 0) {
          console.warn('⚠️ Some stores failed to initialize:', errors)
        }

        console.log('✅ All stores initialized')
        console.log('📊 Current theme:', useSettingsStore.getState().theme)
        setIsInitialized(true)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown initialization error'
        console.error('❌ Initialization failed:', error)
        setInitError(message)
        setIsInitialized(true) // Still show UI even on error
      }
    }

    initialize()
  }, [])

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'fa'
    i18n.changeLanguage(savedLanguage)
    document.documentElement.lang = savedLanguage
    document.documentElement.dir = savedLanguage === 'fa' ? 'rtl' : 'ltr'
  }, [i18n])

  useEffect(() => {
    if (theme === 'dark') {
      console.log('🌙 Applying dark theme')
      document.documentElement.classList.add('dark')
    } else {
      console.log('☀️ Applying light theme')
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">بارگذاری...</p>
        </div>
      </div>
    )
  }

  if (initError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-slate-900">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white mb-2">خطا در بارگذاری</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{initError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            تلاش دوباره
          </button>
        </div>
      </div>
    )
  }

  return <MainLayout />
}

export default function App() {
  return (
    <NavigationProvider>
      <AppContent />
    </NavigationProvider>
  )
}
